import { PropsWithChildren, Ref, RefObject, forwardRef, useContext, useLayoutEffect, useRef } from 'react'
import { useResizeDetector } from 'react-resize-detector'

import { IconButton, Stack, Theme, useMediaQuery } from '@mui/material'
import ArrowBackIosNewTwoToneIcon from '@mui/icons-material/ArrowBackIosNewTwoTone'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'

import { CreateConsultationStepperContext } from '../CreateConsultationStepper'

type CreateConsultationStepLayoutProps = PropsWithChildren<{
  nextButtonDisabled?: boolean
  previousButtonDisabled?: boolean
}>

const BackButton = forwardRef<HTMLButtonElement, { disabled?: boolean; size?: number }>(function BackButton({ disabled = false, size = 70 }, ref) {
  const { activeStep, goPreviousStep } = useContext(CreateConsultationStepperContext)
  return (
    <IconButton
      ref={ref}
      aria-label="previous step"
      sx={{
        backgroundColor: (theme) => theme.palette.common.white,
        border: (theme) => `1px solid ${theme.palette.primary.main}`,
        color: (theme) => theme.palette.primary.main,
        visibility: activeStep > 0 ? 'visible' : 'hidden',
        height: size,
        width: size,
        borderRadius: size,
      }}
      disabled={activeStep === 0 || disabled}
      onClick={goPreviousStep}
      data-testid="previous"
    >
      <ArrowBackIosNewTwoToneIcon style={{ fontSize: 30 }} />
    </IconButton>
  )
})

const NextButton = forwardRef<HTMLButtonElement, { disabled?: boolean; size?: number }>(function NextButton({ disabled = false, size = 70 }, ref) {
  const { activeStep, stepCount, goNextStep } = useContext(CreateConsultationStepperContext)
  return (
    <IconButton
      ref={ref}
      aria-label="next step"
      sx={[
        {
          visibility: activeStep < stepCount - 1 ? 'visible' : 'hidden',
          height: size,
          width: size,
          borderRadius: size,
          '&.Mui-disabled': {
            backgroundColor: (theme) => theme.palette.action.disabledBackground,
          },
        },
        !disabled && {
          background: (theme) => theme.palette.primary.main,
          color: (theme) => theme.palette.common.white,
          '&:hover': { background: (theme) => theme.palette.primary.main },
        },
      ]}
      onClick={goNextStep}
      disabled={disabled}
      data-testid="next"
    >
      <ArrowForwardIosIcon color="inherit" style={{ fontSize: 30 }} />
    </IconButton>
  )
})

export default function CreateConsultationStepLayout(props: CreateConsultationStepLayoutProps) {
  const { children, nextButtonDisabled = false, previousButtonDisabled = false } = props

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const { ref: stepContainerRef, height } = useResizeDetector()
  const nextButtonRef = useRef<HTMLButtonElement>(null)
  const previousButtonRef = useRef<HTMLButtonElement>(null)

  useLayoutEffect(() => {
    if (isMobile) {
      return
    }

    function scrollHandler() {
      if (!stepContainerRef.current || !nextButtonRef.current || !previousButtonRef.current) return

      const { height: stepContainerHeight, y: stepContainerPosY } = stepContainerRef.current.getBoundingClientRect()

      if (stepContainerPosY >= 0) {
        nextButtonRef.current.style.top = previousButtonRef.current.style.top = `${
          (stepContainerHeight + stepContainerPosY > window.innerHeight ? window.innerHeight - stepContainerPosY : stepContainerHeight) / 2
        }px`
      } else {
        nextButtonRef.current.style.top = previousButtonRef.current.style.top = `${
          (window.innerHeight - MAIN_HEADER_HEIGHT) / 2 + MAIN_HEADER_HEIGHT - stepContainerPosY
        }px`
      }
      nextButtonRef.current.style.transform = previousButtonRef.current.style.transform = 'translateY(-50%)'
      nextButtonRef.current.style.transition = previousButtonRef.current.style.transition = 'top 0.05s linear'
    }

    document.addEventListener('scroll', scrollHandler, true)

    scrollHandler()
    return () => {
      document.removeEventListener('scroll', scrollHandler, true)
    }
  }, [height, stepContainerRef, isMobile])

  const SIZE = 50

  return (
    <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" ref={stepContainerRef} spacing={1}>
      {isMobile && (
        <Stack sx={{ p: '32px 16px' }} direction="row" justifyContent={'space-between'}>
          <BackButton ref={previousButtonRef} disabled={previousButtonDisabled} size={SIZE} />
          <NextButton ref={nextButtonRef} disabled={nextButtonDisabled} size={SIZE} />
        </Stack>
      )}
      {!isMobile && <BackButton ref={previousButtonRef} disabled={previousButtonDisabled} />}
      {children}
      {!isMobile && <NextButton ref={nextButtonRef} disabled={nextButtonDisabled} />}
    </Stack>
  )
}
